import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { pipelineApi, opportunityApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../ui/Modal';
import NaicsCodePicker from '../ui/NaicsCodePicker';
import { SET_ASIDE_TYPES, US_STATES, CheckList, MultiLabel, AgencyTree, AgencyOption } from '../filters/pickers';

/**
 * The one editor for what Clevor watches for a user: the criteria the
 * matcher reads after every import, plus the automation switches. Opened
 * from Pursuits ("What I'm watching") and from the New matches panel ("Edit
 * what I'm watching"). The search page's filter modal can replace these
 * criteria with the filters on screen, and says so when it does.
 *
 * Criteria promoted from a saved view are edited on the view; the editor
 * shows them locked, with a way to detach and edit here.
 */
export default function WatchCriteriaModal({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved?: () => void }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [editNaics, setEditNaics] = useState<string[]>([]);
  const [sourceView, setSourceView] = useState<{ id: string; name: string; color: string } | null>(null);
  const [criteriaLocked, setCriteriaLocked] = useState(false);
  const [editAgencies, setEditAgencies] = useState<string[]>([]);
  const [editStates, setEditStates] = useState<string[]>([]);
  const [editSetAsides, setEditSetAsides] = useState<string[]>([]);
  const [agencyOptions, setAgencyOptions] = useState<AgencyOption[]>([]);
  const [agencySearch, setAgencySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [editKeywords, setEditKeywords] = useState('');
  const [editAutoAnalyze, setEditAutoAnalyze] = useState(true);
  const [editAutoMatch, setEditAutoMatch] = useState(true);
  const [editAlertOnMatch, setEditAlertOnMatch] = useState(true);
  const [editWatching, setEditWatching] = useState(true);
  const [editPsc, setEditPsc] = useState('');

  const fetchConfig = async () => {
    try {
      const response = await pipelineApi.getConfig();
      const c = response.data.data || response.data.config;
      if (c) {
        setSourceView(c.sourceView || null);
        setCriteriaLocked(!!c.sourceView);
        setEditNaics(c.naicsCodes || []);
        setEditAgencies(c.agencies || []);
        setEditStates(c.states || []);
        setEditSetAsides(c.setAsides || []);
        setEditKeywords(c.keywords?.join(', ') || '');
        setEditAutoAnalyze(c.autoAnalyze !== false);
        setEditAutoMatch(c.autoMatchVendors !== false);
        setEditAlertOnMatch(c.alertOnMatch !== false);
        setEditWatching(c.watchPaused !== true);
        setEditPsc(c.pscCodes?.join(', ') || '');
      }
    } catch {
      toast.error("Couldn't load what you're watching");
    }
  };

  // Fresh on every open: the search page can replace these criteria while
  // this page is mounted, and a stale form would overwrite that on save.
  useEffect(() => {
    if (open) fetchConfig();
  }, [open]);

  useEffect(() => {
    if (!open || agencyOptions.length) return;
    opportunityApi.getAgencies('Solicitation')
      .then((r) => setAgencyOptions(r.data.agencies || []))
      .catch(() => toast.error("Couldn't load the agency list"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const parseList = (str: string) => str.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
      await pipelineApi.updateConfig({
        // Locked criteria come from the view; sending them would detach it.
        ...(criteriaLocked ? {} : {
          naicsCodes: editNaics,
          agencies: editAgencies,
          states: editStates,
          setAsides: editSetAsides,
          keywords: parseList(editKeywords),
          pscCodes: parseList(editPsc).map((s) => s.toUpperCase()),
        }),
        watchPaused: !editWatching,
        autoAnalyze: editAutoAnalyze,
        autoMatchVendors: editAutoMatch,
        alertOnMatch: editAlertOnMatch,
      });
      toast.success(editWatching ? 'Saved. New postings that fit are reported after each import.' : 'Saved. Watching is off; your criteria are kept.');
      onSaved?.();
      onClose();
    } catch {
      toast.error("Couldn't save what you're watching");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="What I'm watching"
      description="After each import — SAM.gov every hour, and the Florida and Alabama state feeds — every new solicitation that matches all of these is reported to you. Leave a field empty to not filter on it."
      size="lg"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {sourceView && (
          <div className="rounded-token border border-dashed border-border-strong bg-surface-sunk px-3 py-2 text-label text-content">
            These criteria come from your view <Link to={`/opportunities?set=${sourceView.id}`} className="font-semibold text-accent hover:text-accent-hover">{sourceView.name}</Link>.{' '}
            {criteriaLocked
              ? <>Edit the view in Opportunities, or <button type="button" onClick={() => setCriteriaLocked(false)} className="font-medium text-accent underline">edit here instead</button> to detach them.</>
              : <span className="text-content-muted">Editing here detaches them from the view when you save.</span>}
          </div>
        )}
        <fieldset disabled={criteriaLocked} className={`space-y-5 ${criteriaLocked ? 'opacity-70' : ''}`}>
          <div>
            <NaicsCodePicker
              value={editNaics}
              onChange={setEditNaics}
              label="NAICS Codes"
              helpText={user?.naicsCodes?.length
                ? `Leave empty to use your company profile's codes: ${user.naicsCodes.join(', ')}`
                : 'Leave empty to use the codes on your company profile (none set yet)'}
              placeholder='Type your industry, e.g. "HVAC", "236220"…'
            />
          </div>

          <div>
            <MultiLabel id="watch-state-label" text="Place of Performance" count={editStates.length} onClear={() => setEditStates([])} emptyText="Anywhere" />
            <input type="search" aria-label="Search states" placeholder="Search states, e.g. virginia, TX" value={stateSearch} onChange={(e) => setStateSearch(e.target.value)} className="input mb-2" />
            <CheckList
              labelId="watch-state-label"
              options={[
                ...US_STATES.filter((st) => editStates.includes(st.code)),
                ...US_STATES.filter((st) => {
                  if (editStates.includes(st.code)) return false;
                  const q = stateSearch.trim().toLowerCase();
                  return !q || st.label.toLowerCase().includes(q) || st.code.toLowerCase() === q;
                }),
              ]}
              selected={editStates}
              onChange={setEditStates}
              twoColumn
              scroll
            />
          </div>

          <div>
            <MultiLabel id="watch-agency-label" text="Agencies" count={editAgencies.length} onClear={() => setEditAgencies([])} emptyText="Any agency" />
            <input type="search" aria-label="Search agencies" placeholder="Search agencies and sub-agencies, e.g. navy, veterans" value={agencySearch} onChange={(e) => setAgencySearch(e.target.value)} className="input mb-2" />
            <AgencyTree labelId="watch-agency-label" options={agencyOptions} selected={editAgencies} onChange={setEditAgencies} search={agencySearch} />
          </div>

          <div>
            <MultiLabel id="watch-set-aside-label" text="Set-Aside Type" count={editSetAsides.length} onClear={() => setEditSetAsides([])} emptyText="Any set-aside" />
            <CheckList labelId="watch-set-aside-label" options={SET_ASIDE_TYPES} selected={editSetAsides} onChange={setEditSetAsides} scroll />
          </div>

          <div>
            <label htmlFor="watch-psc" className="label">Product Service Codes (PSC)</label>
            <p className="text-caption text-content-subtle mb-1">Comma-separated; a code or a group prefix ("R4" is every R4xx). What the buyer is buying, where NAICS is what you do.</p>
            <input id="watch-psc" type="text" value={editPsc} onChange={(e) => setEditPsc(e.target.value)} className="input" placeholder="e.g. R425, S201, 7G20" />
          </div>

          <div>
            <label htmlFor="watch-keywords" className="label">Keywords</label>
            <p className="text-caption text-content-subtle mb-1">Comma-separated; any of them in the title or description matches</p>
            <input id="watch-keywords" type="text" value={editKeywords} onChange={(e) => setEditKeywords(e.target.value)} className="input" placeholder="e.g. cybersecurity, IT support, construction" />
          </div>
        </fieldset>

        <fieldset className="pt-4 border-t border-border space-y-3">
          <legend className="text-label text-content-muted pb-1">Automation</legend>
          <label className="flex items-center justify-between gap-4">
            <span className="text-body text-content-muted">Watch for new matches <span className="text-caption text-content-subtle">(off keeps your criteria but records and emails nothing)</span></span>
            <input type="checkbox" checked={editWatching} onChange={(e) => setEditWatching(e.target.checked)} className="h-4 w-4 rounded border-border-strong text-accent" />
          </label>
          <label className="flex items-center justify-between gap-4">
            <span className="text-body text-content-muted">Auto-analyze new opportunities</span>
            <input type="checkbox" checked={editAutoAnalyze} onChange={(e) => setEditAutoAnalyze(e.target.checked)} className="h-4 w-4 rounded border-border-strong text-accent" />
          </label>
          <label className="flex items-center justify-between gap-4">
            <span className="text-body text-content-muted">Auto-match vendors for qualified</span>
            <input type="checkbox" checked={editAutoMatch} onChange={(e) => setEditAutoMatch(e.target.checked)} className="h-4 w-4 rounded border-border-strong text-accent" />
          </label>
          <label className="flex items-center justify-between gap-4">
            <span className="text-body text-content-muted">Alert me about new matches <span className="text-caption text-content-subtle">(in-app, and the daily email if Email Alerts is on in Settings)</span></span>
            <input type="checkbox" checked={editAlertOnMatch} onChange={(e) => setEditAlertOnMatch(e.target.checked)} className="h-4 w-4 rounded border-border-strong text-accent" />
          </label>
        </fieldset>
      </div>
    </Modal>
  );
}
